import type { MatchIdentity } from "./AdvancedMatching";

export type {
  BrowserPixelAdapter,
  BrowserEventPayload,
  BrowserPixelEntry,
  BrowserPixelsManifest,
  ScriptErrorReporter,
};
export { injectVendorScript };

/**
 * Contrato de los píxeles de navegador.
 *
 * El SDK NO sabe de negocio: el módulo PHP de cada red (api-marketing,
 * app/Services/AdNetworks/{Red}/) materializa el payload de cada evento en el
 * manifest (`GET /browser-pixel-manifest`, agregado por api-core dentro de
 * config-values como `browserPixels`) y aquí solo se traduce a la llamada
 * nativa del vendor: `fbq` para Meta, `oaiq` para ChatGPT Ads, `whop` para
 * Whop Ads, `ttq` para TikTok Ads, `snaptr` para Snapchat.
 *
 * Los nombres de campo del manifest van en snake_case porque vienen tal cual
 * del backend; nada se renombra al vuelo para que el JSON que viaja sea
 * literalmente lo que se lee aquí.
 */

/** Un disparo concreto: lo que el adaptador manda al vendor sin interpretarlo. */
interface BrowserEventPayload {
  // Nombre del evento EN LA RED (PageView, lead_created, VideoWatched50...)
  name: string;
  // true = evento custom de la red (fbq trackSingleCustom / oaiq 'custom';
  // en Whop y TikTok no cambia la llamada: todo nombre no estándar es custom)
  custom: boolean;
  // Parámetros ya materializados por el backend (Meta: {value, currency};
  // ChatGPT: {type, amount en unidad menor, currency}; Whop: {value,
  // currency en minúsculas}; TikTok: {value, currency}; Snapchat: {price,
  // currency})
  params: Record<string, unknown>;
}

interface BrowserPixelEntry {
  // ad_network.slug del catálogo de marketing (meta_ads, chatgpt_ads...)
  network: string;
  // clave del adaptador del SDK ('meta_pixel' | 'oaiq' | 'whop' | 'gtag' |
  // 'uet' | 'tiktok_pixel' | 'taboola' | 'snap_pixel')
  adapter: string;
  // id PÚBLICO del píxel (lista blanca del manifest: jamás secretos). En
  // Whop, el id de la cuenta (`biz_...`); en Taboola, el id numérico de la cuenta
  pixel_id: string;
  // Opciones de instalación del píxel. Hoy solo `advanced_matching` (bool):
  // a `false`, a ese píxel no se le manda identidad del visitante.
  options?: Record<string, unknown> | null;
  // slug de system_conversion_event -> N disparos (un evento semántico puede
  // mapear a varios eventos de la red en el mismo píxel). SIEMPRE array.
  events: Record<string, BrowserEventPayload[]>;
}

interface BrowserPixelsManifest {
  // Versión del contrato. El SDK ignora entero un manifest de una versión
  // posterior a la que entiende (MANIFEST_VERSION en PixelManager).
  version: number;
  business_unit_id?: number;
  // `null` es un valor REAL del cable, no una anomalía: el serializador del
  // código generado convierte toda colección vacía en `null`, así que una BU
  // sin píxeles de navegador llega como `pixels: null`.
  pixels: BrowserPixelEntry[] | null;
}

interface BrowserPixelAdapter {
  // == BrowserPixelEntry.adapter
  readonly key: string;
  // stub + <script async> del vendor; idempotente; nunca lanza
  load(): void;
  // un init por pixel_id; con identidad, advanced matching según las reglas
  // de la red (Meta en claro, OpenAI pre-hasheado, Whop en claro vía identify,
  // TikTok pre-hasheado vía identify, Snapchat en claro vía re-init)
  init(pixelId: string, identity: MatchIdentity | null): void;
  // Devuelve una promesa SOLO si el adaptador puede saber cuándo ha salido de
  // verdad la petición del vendor (se resuelve entonces y nunca rechaza). El
  // optin la espera, con tope, antes de dejar ir la página. La mayoría manda
  // en el acto y no devuelve nada.
  track(
    pixelId: string,
    event: BrowserEventPayload,
    eventId: string,
  ): Promise<void> | void;
  // ¿Guarda la etiqueta de la red, por su cuenta, un clic de este visitante en
  // uno de sus anuncios? Opcional: solo lo declaran los adaptadores cuyo
  // `track` confirma el envío, para que el optin no espere por un visitante
  // que no viene de esa red.
  hasOwnClick?(): boolean;
  setConsent(granted: boolean): void;
}

type ScriptErrorReporter = (adapterKey: string, error: unknown) => void;

/**
 * Inyecta el script del vendor como hace su snippet oficial (async, antes del
 * primer <script> de la página). El `onerror` es la señal que distingue "ad
 * blocker / CDN caída" de "no se disparó nada": sin él, un bloqueo del vendor
 * es indistinguible de un manifest vacío. `onLoad`, para los vendors cuyo
 * snippet arranca la etiqueta al cargar el script (la UET de Microsoft).
 */
function injectVendorScript(
  src: string,
  adapterKey: string,
  onError?: ScriptErrorReporter | null,
  onLoad?: (() => void) | null,
): void {
  try {
    const script = document.createElement("script");
    script.async = true;
    script.src = src;
    if (onLoad) {
      script.onload = () => {
        try {
          onLoad();
        } catch {
          // el arranque del vendor jamás puede romper la página
        }
      };
    }
    script.onerror = (event) => {
      try {
        onError?.(adapterKey, event);
      } catch (e) {
        // la telemetría jamás puede romper la página
      }
    };
    const firstScript = document.getElementsByTagName("script")[0] ?? null;
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  } catch (e) {
    // sin script del vendor no hay píxel, pero la página sigue intacta
  }
}
